import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Boxes,
  ChevronLeft,
  ChevronRight,
  Edit3,
  ImageIcon,
  Layers3,
  Maximize2,
  Tags,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { StorefrontPreviewLink } from "../../components/StorefrontPreviewLink";
import { Badge, Card, ErrorState, Loading, PageHeader } from "../../components/ui";
import { adminApi, assetUrl } from "../../lib/api";
import { money, number } from "../../lib/format";
import { labelOf } from "../../lib/labels";
import { PERMISSIONS, hasPermission } from "../../lib/permissions";
import { useAuthStore } from "../../store/auth-store";

function htmlToText(value?: string | null): string {
  if (!value) return "—";
  if (typeof window === "undefined") return value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const doc = new DOMParser().parseFromString(value, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim() || "—";
}

export function ProductDetailPage() {
  const user = useAuthStore((state) => state.user);
  const canManage = hasPermission(user, PERMISSIONS.productsManage);
  const { id } = useParams();
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const detail = useQuery({
    queryKey: ["product", id],
    queryFn: () => adminApi.products.detail(id!),
    enabled: Boolean(id),
  });
  const meta = useQuery({
    queryKey: ["product-detail-meta", detail.data?.slug],
    queryFn: () => adminApi.products.list({ search: detail.data!.slug, page: 1, pageSize: 100 }),
    enabled: Boolean(detail.data?.slug),
  });

  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightboxIndex(null);
      if (!detail.data?.images.length) return;
      if (event.key === "ArrowLeft") setLightboxIndex((value) => value === null ? null : (value + 1) % detail.data!.images.length);
      if (event.key === "ArrowRight") setLightboxIndex((value) => value === null ? null : (value - 1 + detail.data!.images.length) % detail.data!.images.length);
    };
    document.addEventListener("keydown", onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [detail.data, lightboxIndex]);

  if (detail.isLoading || meta.isLoading) return <Loading />;
  if (detail.error || !detail.data) return <ErrorState error={detail.error || new Error("محصول پیدا نشد.")} retry={() => void detail.refetch()} />;

  const product = detail.data;
  const adminMeta = meta.data?.items.find((entry) => entry.product.id === product.id);
  const primaryIndex = Math.max(0, product.images.findIndex((image) => image.isPrimary));
  const primaryImage = product.images[primaryIndex] ?? product.images[0];
  const defaultVariant = product.variants.find((variant) => variant.isDefault) ?? product.variants[0];
  const totalStock = product.variants.reduce((sum, variant) => sum + variant.stock, 0);
  const activeGalleryIndex = Math.min(galleryIndex, Math.max(0, product.images.length - 1));
  const galleryImage = product.images[activeGalleryIndex];

  const changeGallery = (direction: 1 | -1) => {
    if (product.images.length <= 1) return;
    setGalleryIndex((current) => (current + direction + product.images.length) % product.images.length);
  };

  return (
    <>
      <PageHeader
        title={product.name}
        eyebrow="جزئیات محصول"
        description="نمای فقط‌خواندنی اطلاعات محصول برای کنترل نهایی؛ هر تغییر از صفحه ویرایش انجام می‌شود."
        actions={(
          <>
            <Link className="button button--ghost" to="/products"><ArrowRight size={17} /> بازگشت</Link>
            <StorefrontPreviewLink path={`/products/${product.slug}`} />
            {canManage ? <Link className="button button--primary" to={`/products/${product.id}`}><Edit3 size={17} /> ویرایش محصول</Link> : null}
          </>
        )}
      />

      <section className="product-detail-hero">
        <button
          type="button"
          className="product-detail-hero__media"
          onClick={() => primaryImage?.url && setLightboxIndex(primaryIndex)}
          aria-label={primaryImage?.url ? "بزرگ‌نمایی تصویر اصلی محصول" : "محصول بدون تصویر"}
        >
          {primaryImage?.url ? (
            <>
              <img src={assetUrl(primaryImage.url)} alt={primaryImage.alt || product.name} />
              <span className="media-zoom-hint"><Maximize2 /> بزرگ‌نمایی</span>
            </>
          ) : <Boxes />}
        </button>
        <div className="product-detail-hero__content">
          <div className="product-detail-hero__badges">
            <Badge value={labelOf(adminMeta?.status ?? "Published")} tone={adminMeta?.status === "Draft" ? "neutral" : adminMeta?.status === "Archived" ? "danger" : "success"} />
            {adminMeta?.isFeatured ? <Badge value="ویژه" tone="warning" /> : null}
            <Badge value={labelOf(product.badge)} tone="info" />
          </div>
          <h2>{product.subtitle || product.name}</h2>
          <p>{product.shortDescription || "برای این محصول توضیح کوتاه ثبت نشده است."}</p>
          <dl className="product-detail-facts">
            <div><dt>برند</dt><dd>{product.brand.name}</dd></div>
            <div><dt>غلظت</dt><dd>{product.concentration}</dd></div>
            <div><dt>جنسیت</dt><dd>{labelOf(product.gender)}</dd></div>
            <div><dt>قیمت پایه</dt><dd>{defaultVariant ? money(defaultVariant.price) : "—"}</dd></div>
          </dl>
        </div>
      </section>

      <div className="product-detail-stats">
        <div><Layers3 /><span>تنوع‌ها</span><strong>{number(product.variants.length)}</strong></div>
        <div><ImageIcon /><span>تصاویر</span><strong>{number(product.images.length)}</strong></div>
        <div><Tags /><span>دسته‌ها</span><strong>{number(product.categories.length)}</strong></div>
        <div><Boxes /><span>موجودی کل</span><strong>{number(totalStock)}</strong></div>
      </div>

      <div className="detail-grid">
        <div className="detail-main">
          <Card title="اطلاعات محصول">
            <dl className="read-only-grid">
              <div><dt>نام</dt><dd>{product.name}</dd></div>
              <div><dt>اسلاگ</dt><dd dir="ltr">/{product.slug}</dd></div>
              <div><dt>برند</dt><dd>{product.brand.name}</dd></div>
              <div><dt>کشور برند</dt><dd>{product.brand.country || "—"}</dd></div>
              <div><dt>خانواده رایحه</dt><dd>{product.family || "—"}</dd></div>
              <div><dt>طبع</dt><dd>{product.nature || "—"}</dd></div>
              <div><dt>فصل پیشنهادی</dt><dd>{product.season || "—"}</dd></div>
              <div><dt>موقعیت استفاده</dt><dd>{product.occasion || "—"}</dd></div>
              <div><dt>کشور سازنده</dt><dd>{product.countryOfOrigin || "—"}</dd></div>
              <div><dt>سال عرضه</dt><dd>{product.releaseYear ? number(product.releaseYear) : "—"}</dd></div>
            </dl>
          </Card>

          <Card title="توضیحات کامل">
            <p className="read-only-copy">{htmlToText(product.description)}</p>
          </Card>

          <Card title="نت‌های بویایی" description="ساختار رایحه در جایگاه اصلی جزئیات محصول نمایش داده می‌شود.">
            <div className="fragrance-notes-grid">
              <div><span>آغازین</span><strong>{product.topNotes || "—"}</strong></div>
              <div><span>میانی</span><strong>{product.middleNotes || "—"}</strong></div>
              <div><span>پایه</span><strong>{product.baseNotes || "—"}</strong></div>
            </div>
          </Card>

          <Card title="تنوع‌ها">
            <div className="variant-read-list">
              {product.variants.map((variant) => (
                <article key={variant.id}>
                  <div>
                    <strong>{variant.volume}</strong>
                    <small dir="ltr">SKU: {variant.sku}</small>
                  </div>
                  <div><span>قیمت</span><b>{money(variant.price)}</b></div>
                  <div><span>موجودی</span><b>{number(variant.stock)}</b></div>
                  <div className="variant-read-list__flags">
                    {variant.isDefault ? <Badge value="پیش‌فرض" tone="success" /> : null}
                    {variant.isTester ? <Badge value="تستر" tone="info" /> : null}
                    {variant.isDecant ? <Badge value="دکانت" tone="warning" /> : null}
                  </div>
                </article>
              ))}
            </div>
          </Card>

          <Card title="گالری محصول" description={product.images.length > 1 ? `${number(product.images.length)} تصویر؛ برای مشاهده تصویر بعدی از کنترل‌ها یا بندانگشتی‌ها استفاده کنید.` : undefined}>
            {galleryImage ? (
              <div className="product-gallery-slider">
                <div className="product-gallery-slider__stage">
                  <button type="button" className="product-gallery-slider__image" onClick={() => setLightboxIndex(activeGalleryIndex)}>
                    <img src={assetUrl(galleryImage.url)} alt={galleryImage.alt || product.name} />
                    <span><Maximize2 /> مشاهده بزرگ</span>
                  </button>
                  {product.images.length > 1 ? (
                    <>
                      <button className="product-gallery-slider__nav product-gallery-slider__nav--prev" type="button" onClick={() => changeGallery(-1)} aria-label="تصویر قبلی"><ChevronRight /></button>
                      <button className="product-gallery-slider__nav product-gallery-slider__nav--next" type="button" onClick={() => changeGallery(1)} aria-label="تصویر بعدی"><ChevronLeft /></button>
                    </>
                  ) : null}
                </div>
                <div className="product-gallery-slider__caption">
                  <span>{galleryImage.alt || `تصویر ${number(activeGalleryIndex + 1)}`}</span>
                  <b>{number(activeGalleryIndex + 1)} / {number(product.images.length)}</b>
                </div>
                {product.images.length > 1 ? (
                  <div className="product-gallery-slider__thumbs">
                    {product.images.map((image, index) => (
                      <button key={`${image.url}-${index}`} type="button" className={index === activeGalleryIndex ? "is-active" : ""} onClick={() => setGalleryIndex(index)}>
                        <img src={assetUrl(image.url)} alt={image.alt || `تصویر ${number(index + 1)}`} />
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : <p className="muted">تصویری ثبت نشده است.</p>}
          </Card>
        </div>

        <aside className="detail-side">
          <Card title="دسته‌بندی‌ها">
            <div className="module-tags">
              {product.categories.length ? product.categories.map((category) => <Badge key={category.id} value={category.name} tone="info" />) : <span className="muted">بدون دسته‌بندی</span>}
            </div>
          </Card>

          <Card title="SEO">
            <dl className="read-only-list">
              <div><dt>عنوان SEO</dt><dd>{product.seo?.title || "خودکار"}</dd></div>
              <div><dt>توضیحات متا</dt><dd>{product.seo?.description || "خودکار"}</dd></div>
              <div><dt>Canonical</dt><dd dir="ltr">{product.seo?.canonicalUrl || "خودکار"}</dd></div>
              <div><dt>Robots</dt><dd>{product.seo?.index ? "Index" : "Noindex"} / {product.seo?.follow ? "Follow" : "Nofollow"}</dd></div>
            </dl>
          </Card>

          <Card title="شناسه‌های تجاری">
            <dl className="read-only-list">
              <div><dt>GTIN</dt><dd dir="ltr">{product.searchMetadata?.gtin || "—"}</dd></div>
              <div><dt>MPN</dt><dd dir="ltr">{product.searchMetadata?.mpn || "—"}</dd></div>
              <div><dt>Google Product Category</dt><dd>{product.searchMetadata?.googleProductCategory || "—"}</dd></div>
            </dl>
          </Card>
        </aside>
      </div>

      {lightboxIndex !== null && product.images[lightboxIndex] ? (
        <div className="image-lightbox" role="dialog" aria-modal="true" aria-label="نمایش بزرگ تصویر محصول" onMouseDown={(event) => event.target === event.currentTarget && setLightboxIndex(null)}>
          <button type="button" className="image-lightbox__close" onClick={() => setLightboxIndex(null)} aria-label="بستن"><X /></button>
          {product.images.length > 1 ? (
            <button type="button" className="image-lightbox__nav image-lightbox__nav--prev" onClick={() => setLightboxIndex((lightboxIndex - 1 + product.images.length) % product.images.length)} aria-label="تصویر قبلی"><ChevronRight /></button>
          ) : null}
          <figure>
            <img src={assetUrl(product.images[lightboxIndex].url)} alt={product.images[lightboxIndex].alt || product.name} />
            <figcaption>{product.images[lightboxIndex].alt || product.name} · {number(lightboxIndex + 1)} از {number(product.images.length)}</figcaption>
          </figure>
          {product.images.length > 1 ? (
            <button type="button" className="image-lightbox__nav image-lightbox__nav--next" onClick={() => setLightboxIndex((lightboxIndex + 1) % product.images.length)} aria-label="تصویر بعدی"><ChevronLeft /></button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
